import { useImagingOrder, useOrder } from '@hook'
import { DeleteModal } from '@ui'
import { getError, showToast } from '@util'
import { Box, Text, TextArea } from 'grommet'
import { useState } from 'react'

type CancelProps = {
  onClose: () => void
  id?: string
}

type ReasonFieldProps = {
  onChange: (value: string) => void
  prompt: string
  value: string
}

const ReasonField = ({ onChange, prompt, value }: ReasonFieldProps) => (
  <Box gap="8px" width="100%">
    <Text color="#999999" size="14px" textAlign="center">
      {prompt}
    </Text>
    <TextArea
      onChange={e => onChange(e.target.value)}
      placeholder="Reason for cancellation"
      resize="vertical"
      rows={3}
      value={value}
    />
  </Box>
)

/** Bundle-level cancel — `PATCH /imaging_orders/:id/cancel` with the doctor's typed reason. */
export const CancelAllImagingOrders = ({ onClose, id }: CancelProps) => {
  const { cancelImagingOrder } = useImagingOrder()
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    const cancellation_reason = reason.trim()
    if (!cancellation_reason) {
      showToast('Enter a reason for cancelling this order')
      return
    }
    setLoading(true)
    try {
      await cancelImagingOrder({ cancellation_reason, id: id as string })
      showToast('Imaging order cancelled successfully')
      onClose()
    } catch (err) {
      getError(err)
    }
    setLoading(false)
  }

  return (
    <DeleteModal
      description={
        <ReasonField
          onChange={setReason}
          prompt="Are you sure you want to cancel all imaging studies on this order?"
          value={reason}
        />
      }
      handleSubmit={handleSubmit}
      isLoading={loading}
      onClose={onClose}
      rBtnLabel="Yes, Cancel"
      title="Cancel imaging order"
    />
  )
}

/**
 * Per-study cancel for a catalog line.
 *
 * The contract exposes only bundle-level cancel, so this falls back to the child
 * `order` row — same approach lab takes, but sending the typed reason.
 */
export const CancelImagingOrder = ({ onClose, id }: CancelProps) => {
  const { updateOrder } = useOrder()
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    const trimmedReason = reason.trim()
    if (!trimmedReason) {
      showToast('Enter a reason for cancelling this study')
      return
    }
    setLoading(true)
    try {
      await updateOrder({
        formVal: { data: { attributes: { note: trimmedReason, status: 'cancelled' }, type: 'order' } },
        id: id as string
      })
      showToast('Imaging study cancelled successfully')
      onClose()
    } catch (err) {
      getError(err)
    }
    setLoading(false)
  }

  return (
    <DeleteModal
      description={
        <ReasonField onChange={setReason} prompt="Are you sure you want to cancel this imaging study?" value={reason} />
      }
      handleSubmit={handleSubmit}
      isLoading={loading}
      onClose={onClose}
      rBtnLabel="Yes, Cancel"
      title="Cancel imaging study"
    />
  )
}
